import { RadioGroup } from "@headlessui/react";
import _, { get } from "lodash";
import type { Survey } from "posthog-js";
import React, { useEffect, useState } from "react";

import { Button, cn } from "@shared/ui";

import StepBody from "../components/StepBody";
import StepFooter from "../components/StepFooter";
import StepHeading from "../components/StepHeading";

const EXIT_SURVEY_NAME = "Exit Survey";

interface ExitSurveyContent {
  id: string;
  question: string;
  choices: string[];
}

// The step renders the first question as single choice, so a survey without choices can't be shown.
const getExitSurveyContent = (
  surveys: Survey[]
): ExitSurveyContent | undefined => {
  // Stopping the survey in PostHog sets end_date; getSurveys returns it anyway.
  const survey = surveys.find(
    ({ name, start_date, end_date }) =>
      name === EXIT_SURVEY_NAME && start_date && !end_date
  );
  const question = survey?.questions[0];
  if (
    !survey ||
    !question ||
    !("choices" in question) ||
    !question.choices?.length
  ) {
    return undefined;
  }
  return {
    id: survey.id,
    question: question.question,
    choices: question.choices,
  };
};

const choiceRadio = (
  options: string[],
  option: string,
  setOption: (value: string) => void
) => {
  return (
    <RadioGroup
      value={option}
      onChange={setOption}
      className="flex flex-col gap-3"
    >
      {options.map((option, index) => (
        <RadioGroup.Option
          key={index}
          value={option}
          className={({ checked }) =>
            cn(
              "flex cursor-pointer items-center gap-4 rounded-lg border p-4 text-left text-ink transition-colors focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink",
              checked
                ? "border-ink bg-surface-muted shadow-[inset_0_0_0_1px_rgb(var(--ax-ink))]"
                : "border-border hover:bg-canvas"
            )
          }
        >
          {({ checked }) => (
            <>
              <span
                aria-hidden
                className={cn(
                  "flex size-5 shrink-0 items-center justify-center rounded-full border bg-surface",
                  checked ? "border-ink" : "border-border"
                )}
              >
                {checked && (
                  <span className="size-2.5 rounded-full bg-ink" />
                )}
              </span>
              <span className="text-body-lg font-medium">{option}</span>
            </>
          )}
        </RadioGroup.Option>
      ))}
    </RadioGroup>
  );
};

interface ExitSurveyProps {
  step: string;
  nextStep: (step: string) => void;
  posthog: any;
  session: any;
}

export const ExitSurvey = ({
  step,
  nextStep,
  posthog,
  session,
}: ExitSurveyProps) => {
  const [survey, setSurvey] = useState<ExitSurveyContent>();
  const [option, setOption] = useState("");

  // releaseCondition: 0-1 value to determine how much of the traffic should see the survey
  // release condition from posthog is not used AT ALL. By default, we set it to 50% if
  // ignition config doesn't contain any exit survey configs
  let releaseCondition = get(
    session,
    "config.exit-survey.releaseCondition",
    0.5
  );

  // If stage is sandbox, set release condition to never
  if (process.env.REACT_APP_STAGE === "sandbox") {
    releaseCondition = 0;
  }

  // Rolled once per visit; re-rolling on every render sent users to exit mid-survey.
  const [randomNumber] = useState(() => _.random(0, 1, true));

  useEffect(() => {
    posthog.capture("$pageview", {
      step,
    });

    // If the random number is GREATER than our release condition,
    // we skip the survey and go to exit
    if (randomNumber > releaseCondition) {
      nextStep("exit");
      return;
    }

    // getSurveys, unlike getActiveMatchingSurveys, doesn't wait on PostHog's lazily
    // loaded surveys script, so it always calls back (with [] on failure).
    let unmounted = false;
    posthog.getSurveys((surveys: Survey[]) => {
      if (unmounted) return;
      const content = getExitSurveyContent(surveys);
      if (!content) {
        console.log("Exit survey unavailable, skipping to exit");
        nextStep("exit");
        return;
      }
      setSurvey(content);
    });

    return () => {
      unmounted = true;
    };
  }, [posthog]);

  useEffect(() => {
    if (survey) {
      posthog.capture("survey shown", {
        $survey_id: survey.id, // required
      });
    }
  }, [survey, posthog]);

  if (!survey) {
    return null;
  }

  const finish = () => {
    if (option) {
      posthog.capture("survey sent", {
        $survey_id: survey.id, // required
        $survey_response: option, // required
      });
    }
    nextStep("exit");
  };

  return (
    <>
      <StepBody>
        <StepHeading title={survey.question} />
        <div className="flex flex-col">
          {choiceRadio(survey.choices, option, setOption)}
        </div>
      </StepBody>

      <StepFooter>
        <Button name="exit-survey-submit" type={"submit"} onClick={finish}>
          Finish
        </Button>
        <Button
          variant="secondary"
          name="exit-survey-skip"
          type={"button"}
          onClick={() => nextStep("exit")}
        >
          Skip survey
        </Button>
      </StepFooter>
    </>
  );
};
